import React from "react";
import { ACTIVITY_TYPE_LABELS } from "../../lib/labels";
import {
  FileText,
  Mail,
  Phone,
  Calendar,
  CheckSquare,
  GitBranch,
  Inbox,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../../lib/format";
import { getAssigneeLabel } from "../../lib/tasks-assignees";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { getActivityContextLinks } from "../../lib/activity-context-links";
import { formatActivityDescription } from "../../lib/activity-description";
import { getActivityDisplayDescription, getActivityDisplayLabel } from "../../lib/activity-display";

const ICON_MAP = {
  FileText,
  Mail,
  Phone,
  Calendar,
  CheckSquare,
  GitBranch,
  Inbox,
};

export default function Timeline({
  items,
  usersById = null,
  emptyText = "Noch keine Aktivitäten.",
  currentContext = null,
  canDelete = false,
  onDelete = null,
}) {
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);

  if (!items || items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">{emptyText}</div>
    );
  }

  async function handleConfirmDelete() {
    if (!pendingDelete || !onDelete) return;
    setDeletingId(pendingDelete.id);
    try {
      await onDelete(pendingDelete);
      setPendingDelete(null);
    } catch {
      // The page-level delete handler already shows the error toast.
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <ol
        data-testid="activity-timeline"
        className="relative ml-3 space-y-4"
      >
        <span
          aria-hidden
          className="absolute left-0 top-1 bottom-1 w-px bg-gradient-to-b from-border via-border to-transparent"
        />
        {items.map((a) => {
          const meta = ACTIVITY_TYPE_LABELS[a.type] || { de: a.type, icon: "FileText" };
          const displayLabel = getActivityDisplayLabel(a) || meta.de;
          const Icon = ICON_MAP[meta.icon] || FileText;
          const authorId = a.created_by_user_id;
          const authorLabel = authorId
            ? getAssigneeLabel(usersById ? usersById[authorId] : null) || "Unbekannt"
            : "";
          const links = getActivityContextLinks(a, { currentContext });
          return (
            <li
              key={a.id}
              data-testid={`activity-item-${a.id}`}
              className="pl-6 relative"
            >
              <span
                className="absolute -left-[7px] top-1 w-4 h-4 rounded-full flex items-center justify-center"
                style={{
                  background: "hsl(var(--card))",
                  boxShadow: "0 0 0 1px var(--brand)",
                }}
              >
                <Icon className="w-2.5 h-2.5" style={{ color: "var(--brand)" }} />
              </span>
              <div
                data-testid={`activity-item-header-${a.id}`}
                className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3"
              >
                <div className="min-w-0 w-full flex-1">
                  <div
                    className="text-[11px] uppercase tracking-wider text-muted-foreground hyphens-auto break-words [overflow-wrap:anywhere]"
                    lang="de"
                  >
                    {displayLabel} · {formatDateTime(a.created_at)}
                  </div>
                  <div
                    data-testid={`activity-item-description-${a.id}`}
                    className="mt-0.5 text-sm hyphens-auto break-words [overflow-wrap:anywhere]"
                    lang="de"
                  >
                    {formatActivityDescription(getActivityDisplayDescription(a))}
                  </div>
                </div>
                {canDelete && onDelete ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Aktivität löschen"
                    onClick={() => setPendingDelete(a)}
                    className="self-start sm:self-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
              {links.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {links.map((l) => (
                    <Button key={l.to} variant="outline" size="sm" asChild>
                      <Link to={l.to}>{l.label}</Link>
                    </Button>
                  ))}
                </div>
              ) : null}
              {authorId ? (
                <div className="text-xs text-muted-foreground mt-0.5">von {authorLabel}</div>
              ) : null}
            </li>
          );
        })}
      </ol>
      <AlertDialog open={Boolean(pendingDelete)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aktivität wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktivität wird ausgeblendet und ist danach nicht mehr in Listen oder Timelines sichtbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDelete(null)}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} disabled={!pendingDelete || deletingId === pendingDelete?.id}>
              {deletingId === pendingDelete?.id ? "Aktivität wird gelöscht …" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
